// src/modules/app-views/_apps/appsindico/units/pages/Document/tabs/HabitationTab.jsx
// *******************************************************************************
// HABITATION TAB
// *******************************************************************************

// *******************************************************************************
// IMPORTS
// *******************************************************************************
import { useState, useCallback, useEffect } from 'react';
import { Row, Col, Card } from 'antd';
import { ROW_GUTTER } from 'constants/ThemeConstant';
import { useDocumentContext } from 'components/core/DocumentProvider';
import { useSimpleFormContext } from 'components/SimpleComponents/SimpleFormProvider';
import SimpleSelect from 'components/SimpleComponents/SimpleSelect';
import SimpleSelectAsyncEntities from 'components/SimpleComponents/SimpleSelectAsyncEntities';
import SimpleDatePicker from 'components/SimpleComponents/SimpleDatePicker';
import SimpleTextArea from 'components/SimpleComponents/SimpleTextArea';
import translateX from 'utils/translateX';

// *******************************************************************************
// MAIN COMPONENT
// *******************************************************************************
const HabitationTab = () => {
  // ─────────────────────────────────────────────────────────────────────────────
  // 1. PROPS, CONTEXTS & GLOBAL STATE
  // ─────────────────────────────────────────────────────────────────────────────
  const { data } = useDocumentContext();
  const { form } = useSimpleFormContext();

  // ─────────────────────────────────────────────────────────────────────────────
  // 4. LOCAL STATE
  // ─────────────────────────────────────────────────────────────────────────────
  const [habitationProfile, setHabitationProfile] = useState(data?.habitationProfile);
  const [contractType, setContractType] = useState(data?.contractType);

  // ─────────────────────────────────────────────────────────────────────────────
  // 5. EFFECTS
  // ─────────────────────────────────────────────────────────────────────────────
  useEffect(() => {
    setHabitationProfile(data?.habitationProfile);
    setContractType(data?.contractType);
  }, [data?.habitationProfile, data?.contractType]);

  // ─────────────────────────────────────────────────────────────────────────────
  // 6. HANDLERS
  // ─────────────────────────────────────────────────────────────────────────────
  const handleProfileChange = useCallback(
    value => {
      setHabitationProfile(value);
      if (value === 'owner') {
        form.setFieldValue('contractType', undefined);
        form.setFieldValue('residentEntityId', undefined);
        setContractType(undefined);
      }
    },
    [form]
  );

  const handleContractChange = useCallback(value => {
    setContractType(value);
  }, []);

  // ─────────────────────────────────────────────────────────────────────────────
  // 7. LOCAL VARIABLES II
  // ─────────────────────────────────────────────────────────────────────────────
  const showResident =
    habitationProfile === 'tenant' || habitationProfile === 'non_tenant_resident';
  const showContract = habitationProfile === 'tenant';
  const showRealEstate = contractType === 'rental_agency';
  const showOwner = showResident;

  // ─────────────────────────────────────────────────────────────────────────────
  // 8. UI
  // ─────────────────────────────────────────────────────────────────────────────
  return (
    <>
      <Card size="small" title={translateX('habitation_profile')} className="mb-3">
        <Row gutter={ROW_GUTTER}>
          <Col xs={24} sm={8}>
            <SimpleSelect
              name="habitationProfile"
              label="profile"
              onChange={handleProfileChange}
              options={[
                { value: 'owner', label: translateX('partner_owner') },
                { value: 'tenant', label: translateX('tenant') },
                { value: 'non_tenant_resident', label: translateX('non_tenant_resident') },
              ]}
            />
          </Col>
          {showContract && (
            <Col xs={24} sm={8}>
              <SimpleSelect
                name="contractType"
                label="contract_type"
                onChange={handleContractChange}
                options={[
                  { value: 'rental_agency', label: translateX('rental_through_agency') },
                  { value: 'rental_direct', label: translateX('rental_direct_owner') },
                  { value: 'ceded', label: translateX('ceded') },
                ]}
              />
            </Col>
          )}
          <Col xs={24} sm={8}>
            <SimpleDatePicker name="moveDate" label="move_date" />
          </Col>
        </Row>
      </Card>

      {showResident && (
        <Card size="small" title={translateX('resident')} className="mb-3">
          <Row gutter={ROW_GUTTER}>
            <Col xs={24}>
              <SimpleSelectAsyncEntities
                name="residentEntityId"
                label="resident"
                placeholder="search_entity"
                entityProfiles={['customer', 'contact']}
              />
            </Col>
          </Row>
        </Card>
      )}

      {showOwner && (
        <Card size="small" title={translateX('partner_owner')} className="mb-3">
          <Row gutter={ROW_GUTTER}>
            <Col xs={24}>
              <SimpleSelectAsyncEntities
                name="ownerEntityId"
                label="owner"
                placeholder="search_entity"
                entityProfiles={['customer', 'contact']}
              />
            </Col>
          </Row>
        </Card>
      )}

      {showRealEstate && (
        <Card size="small" title={translateX('real_estate')}>
          <Row gutter={ROW_GUTTER}>
            <Col xs={24}>
              <SimpleSelectAsyncEntities
                name="realEstateEntityId"
                label="real_estate"
                placeholder="search_entity"
                entityProfiles={['company']}
              />
            </Col>
          </Row>
        </Card>
      )}
    </>
  );
};

// *******************************************************************************
// EXPORT
// *******************************************************************************
export default HabitationTab;
